<script lang="ts" module>
	import type { Snippet } from 'svelte';
	import type { ClassValue, SvelteHTMLElements } from 'svelte/elements';
	import type { VariantProps } from 'tailwind-variants';
	import { tv } from '../tv';
	import { CONTAINER_COLOR_VARIANTS, RADII_VARIANTS } from '../variants';

	export const menuItemVariants = tv({
		base: 'flex w-full cursor-pointer items-center justify-start text-(--v-on-container) hover:bg-(--v-container-hover) focus-visible:bg-(--v-container-hover) active:bg-(--v-container-active) aria-pressed:bg-(--v-container-active) aria-pressed:hover:bg-(--v-container-hover) aria-pressed:focus-visible:bg-(--v-container-hover) aria-pressed:active:bg-(--v-container-active) data-[ui-after=true]:pr-(--menu-item-ui-space) data-[ui-before=true]:pl-(--menu-item-ui-space) focus-outline disabled-state',
		variants: {
			variant: CONTAINER_COLOR_VARIANTS,
			fill: { Soft: 'bg-(--v-container)', None: 'bg-transparent' },
			size: {
				'300': 'h-[2rem] gap-s100 px-s300 [--menu-item-ui-space:var(--spacing-s200)]',
				'400': 'h-[2.5rem] gap-s200 px-s400 [--menu-item-ui-space:var(--spacing-s300)]'
			},
			radii: RADII_VARIANTS
		},
		defaultVariants: { variant: 'Surface', fill: 'Soft', size: '400', radii: '0' }
	});

	export type MenuItemProps<T extends keyof SvelteHTMLElements = 'button'> = Omit<
		SvelteHTMLElements[T],
		'class'
	> &
		VariantProps<typeof menuItemVariants> & {
			after?: Snippet;
			as?: T;
			before?: Snippet;
			children?: Snippet;
			class?: ClassValue;
			ref?: HTMLElement | null;
		};
</script>

<script lang="ts" generics="T extends keyof SvelteHTMLElements = 'button'">
	let {
		after,
		as = 'button' as T,
		before,
		children,
		class: className,
		fill,
		radii,
		ref = $bindable(null),
		size,
		variant,
		...rest
	}: MenuItemProps<T> = $props();
</script>

<svelte:element
	this={as}
	bind:this={ref}
	class={[menuItemVariants({ fill, radii, size, variant }), className]}
	data-ui-after={after ? true : undefined}
	data-ui-before={before ? true : undefined}
	{...rest}
>
	{@render before?.()}{@render children?.()}{@render after?.()}
</svelte:element>
